import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import {
  featureService,
  type Feature,
  type FeatureCategory,
  type FeatureType,
  type OptionItem,
  type FeatureHook,
} from '../../../../services/featureService';
import { SelectField, TextArea } from '../../../../components/ui/Field';

interface Props {
  feature?: Feature;
  onClose: () => void;
  onSaved: () => void;
}

type FormState = {
  name: string;
  key: string;
  description: string;
  type: FeatureType;
  category: FeatureCategory;
  enforcedBy: string;
  displayOrder: string;
  showOnPricingPage: boolean;
  boolDefault: boolean;
  unit: string;
  unlimitedAllowed: boolean;
  minValue: string;
  maxValue: string;
  limitDefault: string;
  options: OptionItem[];
  optionDefault: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const BLANK: FormState = {
  name: '',
  key: '',
  description: '',
  type: 'boolean',
  category: 'access',
  enforcedBy: '',
  displayOrder: '',
  showOnPricingPage: true,
  boolDefault: false,
  unit: '',
  unlimitedAllowed: false,
  minValue: '',
  maxValue: '',
  limitDefault: '1',
  options: [
    { value: 'option_1', label: 'Option 1', sort: 1 },
    { value: 'option_2', label: 'Option 2', sort: 2 },
  ],
  optionDefault: 'option_1',
};

const CATEGORIES: FeatureCategory[] = ['access', 'capacity', 'workflow', 'reporting', 'support', 'integration'];
const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  access: 'Access',
  capacity: 'Capacity',
  workflow: 'Workflow',
  reporting: 'Reporting',
  support: 'Support',
  integration: 'Integration',
};

const TYPE_CARDS: { value: FeatureType; label: string; sub: string }[] = [
  { value: 'boolean', label: 'On / Off', sub: 'Enabled or disabled' },
  { value: 'limit', label: 'Limit', sub: 'A whole number cap' },
  { value: 'option', label: 'Option', sub: 'One choice from a list' },
];

const selectCls = 'w-full rounded-xl border border-agentic-gray-300 bg-white px-4 py-3 text-sm text-agentic-text-primary placeholder:text-agentic-text-muted transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-agentic-gray-100 disabled:text-agentic-text-muted';
const textareaCls = 'w-full rounded-xl border border-agentic-gray-300 bg-white px-4 py-3 text-sm text-agentic-text-primary placeholder:text-agentic-text-muted transition resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2';
const labelCls = 'block text-xs font-semibold text-agentic-text-secondary mb-1.5 uppercase tracking-wide';
const sectionBoxCls = 'rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4 space-y-3';
const fieldErrorCls = 'mt-1 text-xs text-red-500';

function nameToKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors.name = 'Name is required';
  else if (form.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';
  if (!form.key.trim()) errors.key = 'Key is required';
  else if (!/^[a-z][a-z0-9_]{2,49}$/.test(form.key.trim())) errors.key = 'Key must be lowercase snake_case, 3–50 chars';
  if (!form.enforcedBy.trim()) errors.enforcedBy = 'Enforced by is required';
  if (form.type === 'limit' && !form.unit.trim()) errors.unit = 'Unit is required for limit features';
  return errors;
}

const FeatureModal: React.FC<Props> = ({ feature, onClose, onSaved }) => {
  const [form, setForm] = useState<FormState>(BLANK);
  const [saving, setSaving] = useState(false);
  const [lockedFields, setLockedFields] = useState<string[]>([]);
  const [hooks, setHooks] = useState<FeatureHook[]>([]);
  useEffect(() => {
    featureService.listHooks().then(setHooks).catch(() => setHooks([]));
  }, []);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const keyEditedManually = useRef(false);
  const isEdit = !!feature;

  useEffect(() => {
    if (feature) {
      keyEditedManually.current = true; // key is pre-filled on edit, treat as manual
      setForm({
        name: feature.name,
        key: feature.key,
        description: feature.description || '',
        type: feature.type,
        category: feature.category,
        enforcedBy: feature.enforcedBy,
        displayOrder: feature.displayOrder != null ? String(feature.displayOrder) : '',
        showOnPricingPage: feature.showOnPricingPage,
        boolDefault: feature.type === 'boolean' ? Boolean(feature.defaultValue) : false,
        unit: feature.unit || '',
        unlimitedAllowed: feature.unlimitedAllowed || false,
        minValue: feature.minValue != null ? String(feature.minValue) : '',
        maxValue: feature.maxValue != null ? String(feature.maxValue) : '',
        limitDefault: feature.type === 'limit' ? String(feature.defaultValue) : '1',
        options: (feature.options as OptionItem[]) || BLANK.options,
        optionDefault: feature.type === 'option' ? String(feature.defaultValue) : 'option_1',
      });
      featureService.getById(feature.id)
        .then((detail) => setLockedFields(detail.lockedFields))
        .catch(() => {});
    } else {
      keyEditedManually.current = false;
      setForm(BLANK);
      setLockedFields([]);
    }
    setFieldErrors({});
    setTouchedFields(new Set());
  }, [feature]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(
    feature
      ? {
          name: feature.name,
          key: feature.key,
          description: feature.description || '',
          type: feature.type,
          category: feature.category,
          enforcedBy: feature.enforcedBy,
          displayOrder: feature.displayOrder != null ? String(feature.displayOrder) : '',
          showOnPricingPage: feature.showOnPricingPage,
          boolDefault: feature.type === 'boolean' ? Boolean(feature.defaultValue) : false,
          unit: feature.unit || '',
          unlimitedAllowed: feature.unlimitedAllowed || false,
          minValue: feature.minValue != null ? String(feature.minValue) : '',
          maxValue: feature.maxValue != null ? String(feature.maxValue) : '',
          limitDefault: feature.type === 'limit' ? String(feature.defaultValue) : '1',
          options: (feature.options as OptionItem[]) || BLANK.options,
          optionDefault: feature.type === 'option' ? String(feature.defaultValue) : 'option_1',
        }
      : BLANK
  );

  const handleClose = () => {
    if (isDirty) {
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm('You have unsaved changes. Leave anyway?')) return;
    }
    onClose();
  };

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleNameChange = (name: string) => {
    if (!keyEditedManually.current) {
      set({ name, key: nameToKey(name) });
    } else {
      set({ name });
    }
  };

  const handleKeyChange = (key: string) => {
    keyEditedManually.current = true;
    set({ key });
  };

  const touch = (field: keyof FormState) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    const errors = validateForm(form);
    setFieldErrors((prev) => ({ ...prev, [field]: errors[field] }));
  };

  const isLocked = (field: string) => isEdit && lockedFields.includes(field);

  const allErrors = validateForm(form);
  const formValid = Object.keys(allErrors).length === 0;

  const handleOptionChange = (idx: number, patch: Partial<OptionItem>) => {
    const oldValue = form.options[idx]?.value;
    const updated = form.options.map((o, i) => i === idx ? { ...o, ...patch } : o);
    const newValue = updated[idx]?.value;
    const newDefault = (patch.value !== undefined && form.optionDefault === oldValue)
      ? newValue
      : form.optionDefault;
    set({ options: updated, optionDefault: newDefault });
  };

  const addOption = () => {
    const n = form.options.length + 1;
    set({ options: [...form.options, { value: `option_${n}`, label: `Option ${n}`, sort: n }] });
  };

  const removeOption = (idx: number) => {
    if (form.options.length <= 2) return;
    const updated = form.options.filter((_, i) => i !== idx);
    set({ options: updated.map((o, i) => ({ ...o, sort: i + 1 })) });
  };

  const buildPayload = () => {
    const base = {
      name: form.name,
      description: form.description || undefined,
      category: form.category,
      enforcedBy: form.enforcedBy,
      displayOrder: form.displayOrder ? parseInt(form.displayOrder) : undefined,
      showOnPricingPage: form.showOnPricingPage,
    };
    if (form.type === 'boolean') return { ...base, type: 'boolean' as const, defaultValue: form.boolDefault };
    if (form.type === 'limit') {
      return {
        ...base,
        type: 'limit' as const,
        defaultValue: parseInt(form.limitDefault) || 1,
        unit: form.unit,
        unlimitedAllowed: form.unlimitedAllowed,
        minValue: form.minValue ? parseInt(form.minValue) : undefined,
        maxValue: form.maxValue ? parseInt(form.maxValue) : undefined,
      };
    }
    return { ...base, type: 'option' as const, defaultValue: form.optionDefault, options: form.options };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Touch all validated fields to show errors
    const allTouched = new Set<string>(['name', 'key', 'enforcedBy', 'unit']);
    setTouchedFields(allTouched);
    const errors = validateForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      if (isEdit && feature) {
        const updatePayload: any = buildPayload();
        updatePayload.updatedAt = feature.updatedAt;
        if (isLocked('key')) delete updatePayload.key;
        if (isLocked('type')) delete updatePayload.type;
        await featureService.update(feature.id, updatePayload);
        toast.success('Changes saved');
      } else {
        const createPayload: any = { ...buildPayload(), key: form.key || undefined };
        await featureService.create(createPayload);
        toast.success('Feature created');
      }
      onSaved();
    } catch (e: any) {
      const msg: string = e?.message || '';
      if (msg.includes('already taken') || msg.includes('KEY_TAKEN')) {
        toast.error(`Key "${form.key}" is already taken`);
      } else if (msg.includes('STALE_UPDATE') || msg.includes('Someone else saved')) {
        toast.error('Someone else updated this feature. Please reload and try again.');
      } else if (msg.includes('FIELD_LOCKED') || msg.includes('cannot be changed')) {
        toast.error(msg);
      } else {
        toast.error(msg || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const showErr = (field: keyof FormState) =>
    touchedFields.has(field) && fieldErrors[field] ? (
      <p className={fieldErrorCls}>{fieldErrors[field]}</p>
    ) : null;

  return (
    <Modal isOpen onClose={handleClose} title={isEdit ? 'Edit Feature' : 'New Feature'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Name + Key */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Name <span className="text-red-500 normal-case">*</span></label>
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => touch('name')}
              placeholder="e.g. Team seats"
              size="sm"
            />
            {showErr('name')}
          </div>
          <div>
            <label className={labelCls}>
              Key {isLocked('key') && <span className="ml-1 text-amber-500 normal-case font-normal">locked</span>}
            </label>
            <Input
              value={form.key}
              onChange={(e) => handleKeyChange(e.target.value)}
              onBlur={() => touch('key')}
              placeholder="auto-generated from name"
              disabled={isLocked('key')}
              size="sm"
              className="font-mono"
            />
            {showErr('key')}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelCls}>Description</label>
          <TextArea
            className={textareaCls}
            rows={2}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="Optional description (max 280 chars)"
            maxLength={280}
          />
        </div>

        {/* Type radio cards */}
        <div>
          <label className={labelCls}>
            Type {isLocked('type') && <span className="ml-1 text-amber-500 normal-case font-normal">locked</span>}
          </label>
          <div className="grid grid-cols-3 gap-3">
            {TYPE_CARDS.map((card) => {
              const selected = form.type === card.value;
              const locked = isLocked('type');
              return (
                <Button tone="custom"
                  key={card.value}
                  type="button"
                  disabled={locked}
                  onClick={() => !locked && set({ type: card.value })}
                  className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                    selected
                      ? 'border-kv-agentic-blue bg-blue-50'
                      : 'border-agentic-gray-200 bg-white hover:border-agentic-gray-300'
                  } ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <p className={`text-sm font-semibold ${selected ? 'text-kv-agentic-blue' : 'text-agentic-text-primary'}`}>
                    {card.label}
                  </p>
                  <p className="mt-0.5 text-xs text-agentic-text-muted">{card.sub}</p>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Category + Enforced By */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            <SelectField
              className={selectCls}
              value={form.category}
              onChange={(e) => set({ category: e.target.value as FeatureCategory })}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </SelectField>
          </div>
          <div>
            <label className={labelCls}>Enforced By <span className="text-red-500 normal-case">*</span></label>
            {/* The runtime check that applies this feature's plan value. Only hooks that
                handle this feature type are offered. */}
            <SelectField
              className="w-full rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 text-sm"
              value={form.enforcedBy}
              onChange={(e) => set({ enforcedBy: e.target.value })}
              onBlur={() => touch('enforcedBy')}
              disabled={isLocked('enforcedBy')}
            >
              <option value="">Select…</option>
              {hooks
                .filter((h) => h.type === null || h.type === form.type || h.value === form.enforcedBy)
                .map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
            </SelectField>
            {showErr('enforcedBy')}
          </div>
        </div>

        {/* Boolean settings */}
        {form.type === 'boolean' && (
          <div className={sectionBoxCls}>
            <p className="text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Boolean settings</p>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.boolDefault ? 'bg-kv-agentic-blue' : 'bg-agentic-gray-300'}`}
                onClick={() => set({ boolDefault: !form.boolDefault })}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form.boolDefault ? 'translate-x-4' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-agentic-text-primary">
                Default: <span className={`font-medium ${form.boolDefault ? 'text-green-600' : 'text-agentic-text-secondary'}`}>{form.boolDefault ? 'Enabled' : 'Disabled'}</span>
              </span>
            </label>
          </div>
        )}

        {/* Limit settings */}
        {form.type === 'limit' && (
          <div className={sectionBoxCls}>
            <p className="text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Limit settings</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>
                  Unit <span className="text-red-500 normal-case">*</span>
                  {isLocked('unit') && <span className="ml-1 text-amber-500 normal-case font-normal">locked</span>}
                </label>
                <Input
                  value={form.unit}
                  onChange={(e) => set({ unit: e.target.value })}
                  onBlur={() => touch('unit')}
                  placeholder="e.g. seats"
                  disabled={isLocked('unit')}
                  size="sm"
                />
                {showErr('unit')}
              </div>
              <div>
                <label className={labelCls}>Default value</label>
                <Input
                  type="number"
                  value={form.limitDefault}
                  onChange={(e) => set({ limitDefault: e.target.value })}
                  placeholder="1"
                  size="sm"
                  helperText="Use -1 for unlimited"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Min value</label>
                <Input type="number" value={form.minValue} onChange={(e) => set({ minValue: e.target.value })} placeholder="optional" size="sm" />
              </div>
              <div>
                <label className={labelCls}>Max value</label>
                <Input type="number" value={form.maxValue} onChange={(e) => set({ maxValue: e.target.value })} placeholder="optional" size="sm" />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.unlimitedAllowed && !isLocked('unlimitedAllowed') ? 'bg-kv-agentic-blue' : form.unlimitedAllowed ? 'bg-kv-agentic-blue opacity-50' : 'bg-agentic-gray-300'}`}
                onClick={() => !isLocked('unlimitedAllowed') && set({ unlimitedAllowed: !form.unlimitedAllowed })}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form.unlimitedAllowed ? 'translate-x-4' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-agentic-text-primary">
                Allow "unlimited" option (-1)
                {isLocked('unlimitedAllowed') && <span className="ml-1.5 text-xs text-amber-500">(locked)</span>}
              </span>
            </label>
          </div>
        )}

        {/* Option settings */}
        {form.type === 'option' && (
          <div className={sectionBoxCls}>
            <p className="text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Option settings</p>
            <div className="space-y-2">
              {form.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={opt.value}
                    onChange={(e) => handleOptionChange(idx, { value: e.target.value })}
                    placeholder="value"
                    size="sm"
                    className="font-mono w-36 flex-shrink-0"
                  />
                  <Input
                    value={opt.label}
                    onChange={(e) => handleOptionChange(idx, { label: e.target.value })}
                    placeholder="label"
                    size="sm"
                    className="flex-1"
                  />
                  <Button tone="danger-ghost"
                    type="button"
                    onClick={() => removeOption(idx)}
                    disabled={form.options.length <= 2}
                    className="h-8 w-8 flex items-center justify-center rounded-lg transition disabled:opacity-30 disabled:pointer-events-none flex-shrink-0"
                    aria-label="Remove option"
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <Button tone="brand-text"
                type="button"
                onClick={addOption}
                className="text-xs font-medium hover:underline"
              >
                + Add option
              </Button>
            </div>
            <div>
              <label className={labelCls}>Default value</label>
              <SelectField
                className={selectCls}
                value={form.optionDefault}
                onChange={(e) => set({ optionDefault: e.target.value })}
              >
                {form.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} ({o.value})</option>
                ))}
              </SelectField>
            </div>
          </div>
        )}

        {/* Display order + show on pricing */}
        <div className="grid grid-cols-2 gap-4 items-end">
          <div>
            <label className={labelCls}>Display order</label>
            <Input
              type="number"
              value={form.displayOrder}
              onChange={(e) => set({ displayOrder: e.target.value })}
              placeholder="optional"
              size="sm"
            />
          </div>
          <div className="pb-1">
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.showOnPricingPage ? 'bg-kv-agentic-blue' : 'bg-agentic-gray-300'}`}
                onClick={() => set({ showOnPricingPage: !form.showOnPricingPage })}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form.showOnPricingPage ? 'translate-x-4' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-agentic-text-primary">Show on pricing page</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2 border-t border-agentic-gray-100">
          <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={saving || !formValid} loading={saving}>
            {isEdit ? 'Save Changes' : 'Create Feature'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FeatureModal;
